import { ReactNode } from "react";

import { Box } from "@mui/material";

import { ThemedBackgroundShapeConfiguration } from "./useBackgroundShapesConfiguration";

import { useDynamicThemeAwareVariables } from "@/features/FormV2/hooks/useDynamicThemeAwareVariables";
import { ThemedBackgroundShapeTransform } from "@/features/FormV2/sharedComponents/BackgroundShapes/useBackgroundShapesState";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import ShapeDesktop1 from "@/icons/shapes/shape-desktop-1.svg";
import ShapeDesktop2 from "@/icons/shapes/shape-desktop-2.svg";
import ShapeDesktop3 from "@/icons/shapes/shape-desktop-3.svg";
import ShapeDesktop4 from "@/icons/shapes/shape-desktop-4.svg";
import ShapeMobile1 from "@/icons/shapes/shape-mobile-1.svg";
import ShapeMobile2 from "@/icons/shapes/shape-mobile-2.svg";
import ShapeMobile3 from "@/icons/shapes/shape-mobile-3.svg";
import ShapeMobile4 from "@/icons/shapes/shape-mobile-4.svg";

type ThemedBackgroundShapesProps = {
  transforms: { [shapeId: string]: ThemedBackgroundShapeTransform };
  transition: string;
  shapes: ThemedBackgroundShapeConfiguration[];
  children?: ReactNode;
  showShapes?: boolean;
};

export const ThemedBackgroundShapes = ({
  shapes,
  transforms,
  transition,
  children,
  showShapes = false,
}: ThemedBackgroundShapesProps) => {
  const { isSmallScreen } = useMediaQuery();

  const { backgroundColor, shapesBackgroundColor } = useDynamicThemeAwareVariables(({ formsPalette, mode }) => ({
    backgroundColor: mode === "light" ? formsPalette.surface.supershy : formsPalette.surface.intense,
    shapesBackgroundColor: mode === "light" ? formsPalette.surface.quiet : formsPalette.surface.moderate,
  }));

  const getShapeStyle = (shapeId: string) => {
    const shapeTransform = transforms[shapeId] || {
      rotate: 0,
      translateY: 0,
      scale: 1,
    };

    const shapeStyle = shapes.find((s) => s.id === shapeId)?.style;

    const transformString = `translateY(${shapeTransform.translateY}px) rotate(${shapeTransform.rotate}deg) scale(${shapeTransform.scale})`;

    return {
      position: "absolute",
      fill: shapesBackgroundColor,
      ...shapeStyle,
      transform: transformString,
      opacity: shapeTransform.opacity,
      transition,
    };
  };

  const allComponents = [
    { isMobile: true, component: ShapeMobile1, id: "mobileShape1" },
    { isMobile: true, component: ShapeMobile2, id: "mobileShape2" },
    { isMobile: true, component: ShapeMobile3, id: "mobileShape3" },
    { isMobile: true, component: ShapeMobile4, id: "mobileShape4" },
    { isMobile: false, component: ShapeDesktop1, id: "desktopShape1" },
    { isMobile: false, component: ShapeDesktop2, id: "desktopShape4" },
    { isMobile: false, component: ShapeDesktop3, id: "desktopShape3" },
    { isMobile: false, component: ShapeDesktop4, id: "desktopShape2" },
  ];

  const components = allComponents.filter((c) => (isSmallScreen ? c.isMobile : !c.isMobile));

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        zIndex: -1,
        pointerEvents: "none",
        backgroundColor,
      }}
      data-test="background-shapes"
    >
      {showShapes &&
        components.map((c) => {
          const Component = c.component;

          return (
            <Box key={c.id} id={c.id} sx={getShapeStyle(c.id)} data-test={`shape-${c.id}`}>
              <Component />
            </Box>
          );
        })}
      {children && <Box sx={{ position: "relative", zIndex: 1, pointerEvents: "auto" }}>{children}</Box>}
    </Box>
  );
};

ThemedBackgroundShapes.displayName = "ThemedBackgroundShapes";
